/**
 * RoleHierarchyPanel (S2.3) — 角色階層: roles grouped by seniority band, with
 * per-role Axis-A inheritance editing (fleet RoleHierarchyList parity, wired to
 * the REAL resolver fleet never had).
 *
 * Bands are display/sort + colour ONLY — the resolver trusts `inheritsFrom`
 * edges, never rank (plan §0 axis non-conflation), and the panel says so. The
 * effective-grant column is computed client-side by the SAME pure resolver the
 * MSW stamp consumes (`resolveEffectiveAccess` — shared-selector discipline).
 * Acyclic validation reuses the resolver's own `findRoleCycle` (one cycle
 * definition: UI pre-check + mock handler + resolver). Inheritance saves are
 * reason-gated (ReasonRequiredModal → audit row via the client seam).
 *
 * House tenet hide-don't-disable: the `isSystem` admin role renders a lock tag
 * (glyph + full-word tooltip — an explained lock, not a grey-only mystery)
 * INSTEAD of the edit affordance. Create-role routes straight to the RolesTab
 * matrix to set grants (single clone-preset modal, no wizard — plan §Slice 2.3).
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Dropdown,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ApartmentOutlined,
  EditOutlined,
  LockOutlined,
  MoreOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import ReasonRequiredModal from '@/components/common/ReasonRequiredModal';
import { findRoleCycle } from '@/lib/rbac';
import { controlHeightTokens } from '@/styles/tokens';
import { useRoles } from './useSystemGovernance';
import {
  buildHierarchyRows,
  useRoleHierarchyMutations,
  useRoleLevels,
  type HierarchyRow,
} from './useRoleHierarchy';
import CreateRoleModal from './CreateRoleModal';
import LevelManagerModal from './LevelManagerModal';
import RoleLifecycleDialogs, {
  InactiveRoleBanner,
  type RoleLifecycleAction,
} from './RoleLifecycleDialogs';
import type { GovernanceRole } from './model';

const { Text } = Typography;

/** Band colour ladder (AntD preset tag colours, senior → junior). Display only. */
const BAND_COLORS = ['purple', 'geekblue', 'blue', 'cyan', 'green', 'gold'] as const;

interface PanelRow extends HierarchyRow {
  key: string;
  bandName: string | null;
  bandColor: string;
}

const RoleHierarchyPanel = () => {
  const { t } = useTranslation(['governance', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { roles, loading, refresh } = useRoles();
  const { levels, refresh: refreshLevels } = useRoleLevels();
  const { setRoleHierarchy } = useRoleHierarchyMutations();

  const [createOpen, setCreateOpen] = useState(false);
  const [levelsOpen, setLevelsOpen] = useState(false);
  /** The inheritance editor target + its draft edge set. */
  const [editTarget, setEditTarget] = useState<GovernanceRole | null>(null);
  const [draftEdges, setDraftEdges] = useState<string[]>([]);
  const [cyclePath, setCyclePath] = useState<string[] | null>(null);
  const [reasonOpen, setReasonOpen] = useState(false);
  /** S3.4 — the safe-lifecycle dialog target (deactivate/reactivate/delete). */
  const [lifecycleTarget, setLifecycleTarget] = useState<GovernanceRole | null>(null);
  const [lifecycleAction, setLifecycleAction] = useState<RoleLifecycleAction | null>(null);

  const catalog = useMemo(() => roles ?? [], [roles]);
  const nameById = useMemo(
    () => new Map(catalog.map((role) => [role.id, role.name])),
    [catalog]
  );

  const rows: PanelRow[] = useMemo(() => {
    const bands = buildHierarchyRows(catalog, levels ?? []);
    return bands.flatMap((band, bandIndex) =>
      band.roles.map((row) => ({
        ...row,
        key: row.role.id,
        bandName: band.level?.name ?? null,
        bandColor: band.level ? BAND_COLORS[bandIndex % BAND_COLORS.length] : 'default',
      }))
    );
  }, [catalog, levels]);

  const cycleNames = (path: readonly string[]): string =>
    path.map((id) => nameById.get(id) ?? id).join(' → ');

  const openEditor = (role: GovernanceRole) => {
    setEditTarget(role);
    setDraftEdges([...(role.inheritsFrom ?? [])]);
    setCyclePath(null);
  };

  /** S3.4 — open one of the safe-lifecycle dialogs for a role. */
  const openLifecycle = (role: GovernanceRole, action: RoleLifecycleAction) => {
    setLifecycleTarget(role);
    setLifecycleAction(action);
  };

  const closeLifecycle = () => {
    setLifecycleTarget(null);
    setLifecycleAction(null);
  };

  /** Pre-check with the resolver's own cycle definition before the reason gate. */
  const submitEditor = () => {
    if (!editTarget) return;
    const patched = catalog.map((role) =>
      role.id === editTarget.id ? { ...role, inheritsFrom: draftEdges } : role
    );
    const cycle = findRoleCycle(patched);
    if (cycle) {
      setCyclePath([...cycle]);
      return;
    }
    setCyclePath(null);
    setReasonOpen(true);
  };

  const submitReason = async (reason: string) => {
    if (!editTarget) return;
    try {
      await setRoleHierarchy(editTarget, draftEdges, reason);
      message.success(t('hierarchy.updated'));
      setReasonOpen(false);
      setEditTarget(null);
      refresh();
    } catch (error) {
      // Server-side re-validation (same shared definition) — surface its verdict.
      const data =
        error instanceof AxiosError
          ? (error.response?.data as { error?: string; cycle?: string[] })
          : undefined;
      setReasonOpen(false);
      if (data?.error === 'CYCLE_REJECTED') {
        setCyclePath(data.cycle ?? []);
      } else {
        message.error(t('messages.loadFailed'));
      }
    }
  };

  /** Inherit-source options: every active role except self and the system admin. */
  const edgeOptions = useMemo(
    () =>
      catalog
        .filter(
          (role) => role.status === 'active' && !role.isSystem && role.id !== editTarget?.id
        )
        .map((role) => ({ label: role.name, value: role.id })),
    [catalog, editTarget]
  );

  const columns: ColumnsType<PanelRow> = [
    {
      title: t('hierarchy.level'),
      key: 'band',
      width: 130,
      fixed: 'left',
      render: (_, row) => (
        <Tag color={row.bandColor}>{row.bandName ?? t('roleLevels.unbanded')}</Tag>
      ),
    },
    {
      title: t('hierarchy.role'),
      key: 'name',
      width: 220,
      fixed: 'left',
      render: (_, row) => (
        <Space size={token.marginXXS} wrap>
          <Text strong>{row.role.name}</Text>
          {row.role.isSystem && (
            <Tooltip title={t('hierarchy.systemLockedTooltip')} trigger={['hover', 'focus']}>
              <Tag tabIndex={0} icon={<LockOutlined aria-hidden />} color="default">
                {t('roleLevels.systemLocked')}
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: t('hierarchy.inheritsFrom'),
      key: 'inheritsFrom',
      width: 240,
      render: (_, row) =>
        renderDash(
          row.inheritsFromNames.length > 0 ? (
            <Space size={4} wrap>
              {row.inheritsFromNames.map((name) => (
                <Tag key={name} icon={<ApartmentOutlined aria-hidden />}>
                  {name}
                </Tag>
              ))}
            </Space>
          ) : undefined
        ),
    },
    {
      title: t('hierarchy.directCount'),
      key: 'direct',
      width: 120,
      align: 'right',
      render: (_, row) => row.directGrantCount,
    },
    {
      title: (
        <Tooltip title={t('hierarchy.effectiveTooltip')} trigger={['hover', 'focus']}>
          <span tabIndex={0}>{t('hierarchy.effectiveCount')}</span>
        </Tooltip>
      ),
      key: 'effective',
      width: 120,
      align: 'right',
      render: (_, row) =>
        row.effectiveGrantCount > row.directGrantCount ? (
          <Text strong style={{ color: token.colorPrimary }}>
            {row.effectiveGrantCount}
          </Text>
        ) : (
          row.effectiveGrantCount
        ),
    },
    {
      title: t('fields.members'),
      key: 'members',
      width: 100,
      align: 'right',
      render: (_, row) => row.role.memberCount,
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, row) =>
        // Hide-don't-disable: the system admin's lock tag (name column) explains
        // why no edit / lifecycle affordance renders here.
        row.role.isSystem ? null : (
          <Space size={0}>
            <Tooltip title={t('hierarchy.editInheritance')}>
              <Button
                type="text"
                icon={<EditOutlined />}
                aria-label={`${t('hierarchy.editInheritance')} — ${row.role.name}`}
                onClick={() => openEditor(row.role)}
              />
            </Tooltip>
            {/* S3.4 — safe lifecycle: deactivate/reactivate + confirm-gated
                delete, through the orphan pre-check dialogs. */}
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  row.role.status === 'active'
                    ? {
                        key: 'deactivate',
                        label: t('actions.deactivate'),
                        danger: true,
                        onClick: () => openLifecycle(row.role, 'deactivate'),
                      }
                    : {
                        key: 'reactivate',
                        label: t('actions.activate'),
                        style: { color: token.colorSuccess },
                        onClick: () => openLifecycle(row.role, 'reactivate'),
                      },
                  { type: 'divider' as const },
                  {
                    key: 'delete',
                    label: t('lifecycle.deleteSubmit'),
                    danger: true,
                    onClick: () => openLifecycle(row.role, 'delete'),
                  },
                ],
              }}
            >
              <Button
                type="text"
                icon={<MoreOutlined />}
                aria-label={`${t('common:actions.more')} — ${row.role.name}`}
              />
            </Dropdown>
          </Space>
        ),
    },
  ];

  const inactiveRoles = catalog.filter((role) => role.status === 'inactive');

  return (
    <div>
      <div className="tw-mb-4 tw-flex tw-items-start tw-justify-between">
        <Text type="secondary" style={{ maxWidth: 640 }}>
          {t('hierarchy.hint')}
        </Text>
        <Space>
          <Button
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            onClick={() => setLevelsOpen(true)}
          >
            {t('roleLevels.manage')}
          </Button>
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            onClick={() => setCreateOpen(true)}
          >
            {t('createRole.action')}
          </Button>
        </Space>
      </div>

      {/* S3.4 — inactive-role banners: each carries the reactivate AFFORDANCE
          (affordance-not-status); renders nothing while every role is active. */}
      {inactiveRoles.length > 0 && (
        <div
          className="tw-mb-4"
          style={{ display: 'flex', flexDirection: 'column', gap: token.marginXS }}
        >
          {inactiveRoles.map((role) => (
            <InactiveRoleBanner
              key={role.id}
              role={role}
              onReactivate={() => openLifecycle(role, 'reactivate')}
            />
          ))}
        </div>
      )}

      <TableContainer>
        <Table<PanelRow>
          rowKey="key"
          loading={loading}
          columns={columns}
          dataSource={rows}
          rowClassName={(row) => (row.role.status === 'inactive' ? 'inactive-row' : '')}
          pagination={false}
          scroll={{ x: 1000 }}
          locale={{ emptyText: <ListEmptyState title={t('hierarchy.empty')} /> }}
        />
      </TableContainer>

      <CreateRoleModal
        open={createOpen}
        roles={catalog}
        onCancel={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          refresh();
          // The new role sets grants in the matrix and members in Users — the
          // existing surfaces, not duplicated wizard steps (plan §Slice 2.3).
          navigate('/auth/governance/roles');
        }}
      />

      <LevelManagerModal
        open={levelsOpen}
        roles={catalog}
        onCancel={() => setLevelsOpen(false)}
        onChanged={() => {
          refreshLevels();
          refresh();
        }}
      />

      <Modal
        open={Boolean(editTarget)}
        title={t('hierarchy.editInheritanceTitle', { name: editTarget?.name ?? '' })}
        okText={t('actions.save')}
        cancelText={t('actions.cancel')}
        onCancel={() => setEditTarget(null)}
        onOk={submitEditor}
        okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
        cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
        maskClosable={false}
        destroyOnHidden
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: token.marginSM }}>
          <Text type="secondary">{t('createRole.inheritsFromHint')}</Text>
          <Select
            mode="multiple"
            size="large"
            style={{ width: '100%' }}
            placeholder={t('hierarchy.none')}
            value={draftEdges}
            onChange={(next) => {
              setDraftEdges(next);
              setCyclePath(null);
            }}
            options={edgeOptions}
          />
          {cyclePath && (
            <Alert
              type="error"
              showIcon
              message={t('cycleRejected', { path: cycleNames(cyclePath) })}
            />
          )}
        </div>
      </Modal>

      {/* S3.4 — safe lifecycle dialogs (orphan pre-check, transfer, confirm-text). */}
      <RoleLifecycleDialogs
        role={lifecycleTarget}
        action={lifecycleAction}
        onClose={closeLifecycle}
        onChanged={refresh}
      />

      <ReasonRequiredModal
        open={reasonOpen}
        title={t('hierarchy.reasonTitle')}
        submitText={t('reasonModal.submit')}
        cancelText={t('reasonModal.cancel')}
        reasonLabel={t('reasonModal.reasonLabel')}
        requiredMessage={t('reasonModal.reasonRequired')}
        reasonPlaceholder={t('reasonModal.reasonPlaceholder')}
        onSubmit={submitReason}
        onCancel={() => setReasonOpen(false)}
      />
    </div>
  );
};

export default RoleHierarchyPanel;
